"use client";

import { useId, useState, type ReactNode } from "react";
import { useDroppable } from "@dnd-kit/core";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import LomdaUploadFile from "./lomdaUploadFile";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { TAB_ICON_KEYS, TabIcon } from "../../../lib/tabIcons";
import { DragData } from "../../../lib/dropTarget";
import { ICON_UPLOAD, MAX_CUSTOM_ICONS } from "../../../lib/uploadLimits";

const EMPTY_ICONS: string[] = [];

/**
 * The one icon picker for every block that has an icon (flip cards, tabs).
 *
 * Three rows: the built-in icons, an upload button, then the brand's own
 * uploaded icons. Uploads land on the lomda's theme snapshot, so an icon
 * uploaded on a flip card is immediately offered on a tab too — and the brand
 * kit's "Icons" tab seeds that list for every new lomda.
 */
export function IconPicker({
    value,
    onPick,
    children,
    align = "start",
}: {
    value: string;
    onPick: (icon: string) => void;
    /** The trigger — each block styles its own icon button. */
    children: ReactNode;
    align?: "start" | "center" | "end";
}) {
    const userId = useLomdaV3Editor((s) => s.userId);
    const customIcons =
        useLomdaV3Editor((s) => s.metadata.theme?.customIcons) ?? EMPTY_ICONS;
    const addCustomIcons = useLomdaV3Editor((s) => s.addCustomIcons);
    const [open, setOpen] = useState(false);
    // Remounts the uploader after each upload so the next one starts clean.
    const [uploadCount, setUploadCount] = useState(0);
    const slot: DragData = { kind: "icon-slot", set: onPick };
    const { setNodeRef, isOver } = useDroppable({
        id: `icon-slot:${useId()}`,
        data: slot,
    });

    const pick = (icon: string) => {
        onPick(icon);
        // Picking is the end of the task — leaving the popover open just hides
        // the card the user is styling.
        setOpen(false);
    };

    const handleUploaded = (url: string) => {
        // The path is keyed by the file, so re-uploading an icon that is
        // already in the list just selects it instead of adding a copy.
        addCustomIcons([url]);
        pick(url);
        setUploadCount((n) => n + 1);
    };

    const iconButton = (icon: string) => (
        <button
            key={icon}
            type="button"
            onClick={() => pick(icon)}
            className={cn(
                "flex aspect-square items-center justify-center rounded-md p-1 text-muted-foreground transition hover:bg-muted",
                value === icon && "bg-primary/10 text-primary"
            )}
        >
            <TabIcon name={icon} className="size-5" />
        </button>
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            {/* A library icon dragged from the Media panel can be dropped here. */}
            <span
                ref={setNodeRef}
                className={cn(
                    "inline-flex w-fit shrink-0 rounded-lg transition",
                    isOver &&
                        "ring-2 ring-[hsl(var(--platform-primary))] ring-offset-2"
                )}
            >
                <PopoverTrigger asChild>{children}</PopoverTrigger>
            </span>
            <PopoverContent align={align} className="w-64 p-3">
                <div className="flex flex-col gap-2">
                    <span className="text-xs font-medium text-muted-foreground">
                        {t("lomda-v3-editor.icons.default")}
                    </span>
                    <div className="grid grid-cols-5 gap-1">
                        {TAB_ICON_KEYS.map(iconButton)}
                    </div>

                    {customIcons.length > 0 && (
                        <>
                            <span className="text-xs font-medium text-muted-foreground">
                                {t("lomda-v3-editor.icons.custom")}
                            </span>
                            <div className="grid max-h-40 grid-cols-5 gap-1 overflow-y-auto">
                                {customIcons.map(iconButton)}
                            </div>
                        </>
                    )}
                    {userId && customIcons.length < MAX_CUSTOM_ICONS && (
                        <>
                            <div className="border-t" />
                            <LomdaUploadFile
                                key={uploadCount}
                                folder="icons"
                                record={false}
                                options={{
                                    ...ICON_UPLOAD,
                                    showImagePreview: false,
                                    showSelectedFileName: false,
                                    purgeCache: true,
                                    className: "h-16 w-full",
                                    title: {
                                        text: t("lomda-v3-editor.icons.upload"),
                                        keyName: "lomda-v3-editor.icons.upload",
                                    },
                                    subtitle: {
                                        text: "PNG, SVG",
                                        keyName:
                                            "brand-kits.icons.upload-subtitle",
                                    },
                                }}
                                onUploadFinished={(f) =>
                                    handleUploaded(f.fileUrl)
                                }
                            />
                        </>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
